/**
 * GraphQL queries and hooks — process pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import { useAllPages } from './useAllPages';
import type {
  BusinessProcess,
  BusinessProcessConnection,
  BusinessProcessFilter,
  PhysicalProcess,
  PhysicalProcessConnection,
  PhysicalProcessFilter,
} from '../types/process';

export const BUSINESS_PROCESS_FIELDS = gql`
  fragment BusinessProcessFields on BusinessProcess {
    entityId
    entityType
    name
    description
    processType
    processTier
    processOwnerId
    owningBusinessUnitId
    inputDescription
    outputDescription
    estimatedDuration
    volumePerPeriod
    automationLevel
    toolingDescription
    status
    tags
    notes
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

export const GET_BUSINESS_PROCESS = gql`
  ${BUSINESS_PROCESS_FIELDS}
  query GetBusinessProcess($id: ID!) {
    businessProcess(id: $id) {
      ...BusinessProcessFields
    }
  }
`;

export const LIST_BUSINESS_PROCESSES = gql`
  ${BUSINESS_PROCESS_FIELDS}
  query ListBusinessProcesses($filter: BusinessProcessFilter, $first: Int, $after: String) {
    businessProcesses(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          ...BusinessProcessFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export function useBusinessProcess(
  id: string
): QueryResult<{ businessProcess: BusinessProcess | null }> {
  return useQuery(GET_BUSINESS_PROCESS, {
    variables: { id },
    skip: !id,
  });
}

export function useBusinessProcesses(
  filter?: BusinessProcessFilter,
  first: number = 250
): QueryResult<{ businessProcesses: BusinessProcessConnection }> {
  return useAllPages<{ businessProcesses: BusinessProcessConnection }>(
    LIST_BUSINESS_PROCESSES,
    (data) => data?.businessProcesses,
    { filter },
    first
  );
}

export const PHYSICAL_PROCESS_FIELDS = gql`
  fragment PhysicalProcessFields on PhysicalProcess {
    entityId
    entityType
    name
    description
    processType
    processTier
    processOwnerId
    owningBusinessUnitId
    inputDescription
    outputDescription
    estimatedDuration
    volumePerPeriod
    automationLevel
    toolingDescription
    status
    tags
    notes
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

export const GET_PHYSICAL_PROCESS = gql`
  ${PHYSICAL_PROCESS_FIELDS}
  query GetPhysicalProcess($id: ID!) {
    physicalProcess(id: $id) {
      ...PhysicalProcessFields
    }
  }
`;

export const LIST_PHYSICAL_PROCESSES = gql`
  ${PHYSICAL_PROCESS_FIELDS}
  query ListPhysicalProcesses($filter: PhysicalProcessFilter, $first: Int, $after: String) {
    physicalProcesses(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          ...PhysicalProcessFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export function usePhysicalProcess(
  id: string
): QueryResult<{ physicalProcess: PhysicalProcess | null }> {
  return useQuery(GET_PHYSICAL_PROCESS, {
    variables: { id },
    skip: !id,
  });
}

export function usePhysicalProcesses(
  filter?: PhysicalProcessFilter,
  first: number = 250
): QueryResult<{ physicalProcesses: PhysicalProcessConnection }> {
  return useAllPages<{ physicalProcesses: PhysicalProcessConnection }>(
    LIST_PHYSICAL_PROCESSES,
    (data) => data?.physicalProcesses,
    { filter },
    first
  );
}
